/**
 * WF-Email - Email input component for wizard forms
 *
 * A convenience component for email inputs with optional work email validation.
 *
 * @example
 * <wf-email name="email" label="Email Address" work-email required />
 */

import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { FormInputBase } from './base/form-input-base.js';
import { ValidationController, DEFAULT_BLOCKED_DOMAINS } from '../controllers/ValidationController.js';

@customElement('wf-email')
export class WfEmail extends FormInputBase {

  // ============================================
  // Email-specific Properties
  // ============================================

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder = 'you@company.com';

  /**
   * Whether to validate as work email (block personal domains)
   */
  @property({ type: Boolean, attribute: 'work-email' })
  workEmail = false;

  /**
   * Custom blocked domains (comma-separated or array)
   */
  @property({ type: String, attribute: 'blocked-domains' })
  blockedDomains = '';

  /**
   * Work email validation message
   */
  @property({ type: String, attribute: 'work-email-message' })
  workEmailMessage = 'Please use your work email address';

  /**
   * Whether field is disabled
   */
  @property({ type: Boolean })
  disabled = false;

  // ============================================
  // Abstract Method Implementations
  // ============================================

  protected _getValidationTriggerProperties(): string[] {
    return ['required', 'workEmail', 'blockedDomains'];
  }

  /**
   * Email uses faster debounce (300ms) for responsive validation
   */
  protected override _getDebounceMs(): number {
    return 300;
  }

  protected _setupValidators(): void {
    this._validators = [];

    // Required validator
    if (this.required) {
      this._validators.push(ValidationController.required());
    }

    // Email format validator (always applied)
    this._validators.push(ValidationController.email());

    // Work email validator
    if (this.workEmail) {
      let domains = DEFAULT_BLOCKED_DOMAINS;

      if (this.blockedDomains) {
        domains = this.blockedDomains.split(',').map((d) => d.trim().toLowerCase());
      }

      this._validators.push(
        ValidationController.workEmail(domains, this.workEmailMessage)
      );
    }
  }

  // ============================================
  // Render
  // ============================================

  render() {
    return html`
      <div class="wf-field-container">
        ${this._renderLabel()}
        <input
          type="email"
          class="wf-input ${this._errorMessage ? 'wf-input-error' : ''}"
          id="${this.name}"
          name="${this.name}"
          placeholder="${this.placeholder}"
          .value="${this._value}"
          ?disabled="${this.disabled}"
          ?required="${this.required}"
          data-testid="wf-input"
          @input="${this._handleInput}"
          @blur="${this._handleBlur}"
          @focus="${this._handleFocus}"
        />
        ${this._renderHint()}
        ${this._renderError()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-email': WfEmail;
  }
}
